import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MonPromptService } from '@monsido/angular-shared-components';
import { ShadowAwareMonPromptService } from '@client/app/modules/modals/services/shadow-aware-mon-prompt/shadow-aware-mon-prompt.service';
import { ErrorHandler, inject, NgModule, provideAppInitializer, CSP_NONCE, Injector } from '@angular/core';
import { LoaderModule } from '@monsido/core/loader/loader.module';
import { LoadingModule } from '@monsido/services/loading/loading.module';
import { MonEventModule } from '@monsido/services/mon-event/mon-event.module';
import { MonBiModule } from '@client/app/modules/business-intelligence/bi.module';
import { MonScoreComponentsModule } from './modules/score/components/mon-score-components.module';
import { MonScoreServicesModule } from './modules/score/services/mon-score-services.module';
import { MonBrowserModule } from '@monsido/services/mon-browser/mon-browser.module';
import { MonLegacyBrowserModule } from '@monsido/modules/legacy-browsers/legacy-browser.module';
import { MonCookieBannerModule } from '@monsido/modules/cookie-banner/cookie-banner.module';
import { RollbarService, RollbarErrorHandlerService, rollbarFactory } from '../ng2/external/rollbar/rollbar-error-handler.service';
import { OverlayService } from '@monsido/services/overlay-service/overlay.service';
import { AccessibilityComponentsModule } from './modules/accessibility/components/accessibility-components.module';
import { MonIssueModule } from '@monsido/modules/issue/issue.module';
import { QaModule } from './modules/qa/qa.module';
import { PageDetailsModule } from '@monsido/modules/page-details/page-details.module';
import { DataPrivacyModule } from '@monsido/modules/data-privacy/data-privacy.module';
import { MonLabelsModule } from '@monsido/modules/labels/labels.module';
import { ActiveFeatureModule } from '@monsido/services/active-feature/active-feature.module';
import { OverlayWrapperModule } from './modules/overlay-wrapper/overlay-wrapper.module';
import { DirectNavigationService } from '@monsido/services/direct-navigation-service/direct-navigation.service';
import { CustomerHasPlanPipe } from '@monsido/ng2/shared/pipes/customer-has-plan/customer-has-plan.pipe';
import { StatisticsModule } from '@monsido/modules/statistics/statistics.module';
import { IsStatsDemoDataPipe } from '@client/app/shared-modules/pipes/is-stats-demo-data.pipe';
import { ReportCenterModule } from '@monsido/modules/report-center/report-center.module';
import { RolePipe } from '@monsido/core/pipes/role/role.pipe';
import { PipeService } from './filters/pipe-transformation.service';
import { MonInventoryModule } from '@monsido/ng2/modules/inventory/inventory.module';
import { UIRouterModule, UIROUTER_MODULE_TOKEN } from '@uirouter/angular';
import {
    NgbDateAdapter,
    NgbDateNativeAdapter,
    NgbModule, NgbTimeAdapter,
    NgbTimeStruct,
    NgbTooltipConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { TrackingModule } from '@client/app/modules/tracking/tracking.module';
import { TranslateModule } from '@client/app/modules/translate.module';
import { ExportModule } from '@client/app/modules/export/export.module';
import { AdminModule } from '@client/app/feature-modules/admin/admin.module';
import { NotificationsStates } from '@client/app/feature-modules/navigation/notifications/states/notifications.state';
import { providePrimeNG } from 'primeng/config';
import { UseStyle } from 'primeng/usestyle';
import { OptimizePreset } from '@client/theme';
import { PagePerformanceModule } from '@client/app/modules/page-performance/page-performance.module';
import { QuickHelpComplianceStatusModule } from '@client/app/components/quick-help-compliance-status/quick-help-compliance-status.module';
import { TermsModule } from '@client/app/modules/terms/terms.module';
import { CacheModule } from './modules/cache/cache.module';
import { ChatModule } from './modules/chat/chat.module';
import { SpotlightModule } from './modules/spotlight/spotlight.module';
import { MomentsModule } from './modules/moments/moments.module';
import { DeeplinkModule } from './modules/deeplink/deeplink.module';
import { ModalsModule } from './modules/modals/modals.module';
import { TranslocoRootModule } from './transloco-root.module';
import { PrimeNGTranslationService } from './services/primeng-translation.service';
import { ExtensionModule } from './modules/extension/extension.module';
import { NotificationsModule } from './modules/notifications/notifications.module';
import { ChartsModule } from './modules/charts/charts.module';
import { UpgradePipeModule } from '@client/app/filters/upgrade-pipe.module';
import { PagesModule } from '@client/app/pages/pages.module';
import {
    DialogUpgradeWrapperComponent,
} from '@client/app/modules/dialog-upgrade-wrapper/dialog-upgrade-wrapper.component';
import { ComponentsModule } from './components/components.module';
import { UpgradeFormsModule } from '@client/app/forms/forms.module';
import { DomainFormModule } from '@client/app/forms/domain/domain-form.module';
import { UpgradeDataPrivacyModule } from '@client/app/modules/data-privacy/data-privacy.module';
import { UpgradeGlobalModule } from '@client/app/pages/global/global.module';
import { UpgradePolicyModule } from '@client/app/pages/policy/policy.module';
import { UpgradeDomainModule } from '@client/app/pages/domain/domain.module';
import { UpgradeDashboardModule } from '@client/app/pages/dashboard/dashboard.module';
import { UpgradeUptimesModule } from '@client/app/pages/uptimes/uptimes.module';
import { UpgradeHeatmapsModule } from '@client/app/modules/heatmaps/heatmaps.module';
import { UpgradeLayoutModule } from '@client/app/layout/layout.module';
import { UIRouter } from '@uirouter/core';
import { LAYOUT_STATES } from '@client/app/layout/layout.route';
import { AppComponent } from '@client/app.component';
import { behaviorEventListeners, globalEventListeners } from '@client/app/core/run/setup.run';
import { uiRouterHooks } from '@client/app/core/run/basic.run';
import { DASHBOARD_STATES } from '@client/app/pages/dashboard/dashboard.route';
import { LOGIN_STATES } from '@client/app/pages/login/login.route';
import { UpgradeLoginModule } from '@client/app/pages/login/login.module';
import { DialogContainerModule } from '@monsido/core/modules/dialog-container/dialog-container.module';
import { ADMIN_STATES } from '@client/app/pages/admin/admin.route';
import { DOMAIN_STATES } from '@client/app/pages/domain/domain.route';
import { GLOBAL_STATES } from '@client/app/pages/global/global.route';
import { GLOBAL_POLICY_STATES } from '@client/app/pages/global/policy/global-policy.route';
import { HEATMAPS_STATES } from '@client/app/modules/heatmaps/pages/heatmaps.route';
import { POLICY_STATES } from '@client/app/pages/policy/policy.route';
import { QA_STATES } from '@client/app/pages/qa/qa.route';
import { QA_BROKEN_LINKS_STATES } from '@client/app/pages/qa/broken-links/broken.route';
import { QA_SPELL_CHECK_STATES } from '@client/app/pages/qa/spellcheck/spellcheck.route';
import { QA_READABILITY_STATES } from '@client/app/pages/qa/readability/readability.route';
import { QA_LANGUAGE_MATCH_STATES } from '@client/app/pages/qa/language-match/language-match.route';
import { createAccessibilityFutureState } from '@client/app/pages/accessibility/accessibility-future.state';
import { UPTIMES_STATES } from '@client/app/pages/uptimes/uptimes.route';
import { PAGE_FIX_STATES } from '@client/app/modules/page-fix/pages/page-fix.route';
import { NavigationModule } from '@client/app/feature-modules/navigation/navigation.module';
import { DATA_PRIVACY_STATES } from '@client/app/modules/data-privacy/pages/pages.route';
import { PAGE_PERFORMANCE_STATES } from '@client/app/modules/page-performance/page-performance.route';
import { AppClockService } from '@client/app/core/services/app-clock.service';
import { ThemeService } from '@client/app/services/theme.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ShadowOverlayContainer } from '@client/mfe/overlay/shadow-overlay-container';
import { ShadowDomStylesHostService } from '@client/mfe/styles/shadow-dom-styles-host';
import { ShadowDomPrimeNgUseStyleService } from '@client/mfe/styles/shadow-dom-primeng-use-style.service';
import { initEventBusBridge } from '@client/app/services/event-bus-bridge.init';
import { IS_MICROFRONTEND, MFE_BASE_URL_TOKEN } from '@client/mfe/tokens/mfe-tokens';
import { MfeDataService } from '@monsido/core/mfe/mfe-data.service';
import { buildRollbarContext } from '../ng2/external/rollbar/rollbar-context.util';

@NgModule({
    declarations: [
        DialogUpgradeWrapperComponent,
        AppComponent,
    ],
    exports: [
        DialogUpgradeWrapperComponent,
    ],
    imports: [
        TermsModule,
        TranslocoRootModule,
        PagePerformanceModule,
        ExportModule,
        TranslateModule,
        QuickHelpComplianceStatusModule,
        LoaderModule,
        LoadingModule,
        BrowserAnimationsModule,
        CommonModule,
        UpgradeFormsModule,
        MonEventModule,
        MonBiModule,
        TrackingModule,
        MonScoreComponentsModule,
        MonScoreServicesModule,
        MonBrowserModule,
        MonLegacyBrowserModule,
        MonCookieBannerModule,
        QaModule,
        AccessibilityComponentsModule,
        MonIssueModule,
        MonInventoryModule,
        PageDetailsModule,
        DataPrivacyModule,
        MonLabelsModule,
        OverlayWrapperModule,
        ReportCenterModule,
        StatisticsModule,
        ActiveFeatureModule,
        NgbModule,
        AdminModule,
        NavigationModule,
        CacheModule,
        ChatModule,
        SpotlightModule,
        MomentsModule,
        DeeplinkModule,
        ModalsModule,
        ExtensionModule,
        NotificationsModule,
        ChartsModule,
        UpgradePipeModule,
        ComponentsModule,
        DomainFormModule,
        UpgradeDataPrivacyModule,
        UpgradeGlobalModule,
        UpgradePolicyModule,
        UpgradeDomainModule,
        UpgradeDashboardModule,
        UpgradeUptimesModule,
        UpgradeHeatmapsModule,
        UpgradeLayoutModule,
        UpgradeLoginModule,
        UIRouterModule,
        UIRouterModule.forRoot({
            states: [
                ...LAYOUT_STATES,
            ],
            config: (router: UIRouter, injector: Injector) => {
                const isMFE = injector.get(IS_MICROFRONTEND, null);
                if (isMFE) {
                    // MFE mode: navigation is managed manually in webgov-element.ts.
                    // deferIntercept() prevents the bootstrap-time sync from firing
                    // validateDomain against a stale URL during an org-switch remount.
                    router.urlService.deferIntercept();
                    return;
                }

                // Standalone mode: normal routing behavior
                router.urlService.rules.initial({ state: 'base.dashboard' });
                router.urlService.rules.otherwise({ state: 'base.dashboard' });
            },
        }),
        UIRouterModule.forChild({
            states: [
                ...LOGIN_STATES,
                ...DOMAIN_STATES,
                ...DASHBOARD_STATES,
                ...ADMIN_STATES,
                ...GLOBAL_POLICY_STATES,
                ...GLOBAL_STATES,
                ...HEATMAPS_STATES,
                ...POLICY_STATES,
                ...QA_STATES,
                ...QA_BROKEN_LINKS_STATES,
                ...QA_SPELL_CHECK_STATES,
                ...QA_READABILITY_STATES,
                ...QA_LANGUAGE_MATCH_STATES,
                ...UPTIMES_STATES,
                ...PAGE_FIX_STATES,
                ...DATA_PRIVACY_STATES,
                ...PAGE_PERFORMANCE_STATES,
                ...NotificationsStates,
            ],
        }),
        DialogContainerModule,
        PagesModule,
    ],
    providers: [
        RollbarErrorHandlerService,
        { provide: MonPromptService, useClass: ShadowAwareMonPromptService },
        OverlayService,
        DirectNavigationService,
        { provide: ErrorHandler, useClass: RollbarErrorHandlerService },
        { provide: RollbarService, useFactory: rollbarFactory },
        CustomerHasPlanPipe,
        IsStatsDemoDataPipe,
        RolePipe,
        PipeService,
        provideAppInitializer(() => {
            globalEventListeners();
            behaviorEventListeners();
            uiRouterHooks();
            const appClockService = inject(AppClockService);
            appClockService.start();
            inject(ThemeService);
            initEventBusBridge();

            // In MFE mode, all ng-bootstrap tooltips (and popovers) must have
            // container='body' so they are appended via document.body.appendChild,
            // which our patches.ts intercepts and redirects into mfe-root.
            // Without this, tooltips are positioned absolutely inside the shadow
            // DOM relative to the contain:layout boundary, ending up at top-left.
            const isMfe = inject(IS_MICROFRONTEND, { optional: true });
            if (isMfe) {
                const tooltipConfig = inject(NgbTooltipConfig);
                tooltipConfig.container = 'body';
            }

            // Tag every Rollbar item with the mode (mfe | standalone) and our bundle URL,
            // so items can be filtered and `inApp` can be derived (see rollbar-context.util).
            inject(RollbarErrorHandlerService).setConfigPayload({
                custom: buildRollbarContext({
                    isMicrofrontend: isMfe === true,
                    mfeBaseUrl: inject(MFE_BASE_URL_TOKEN, { optional: true }),
                    hostPageOrigin: window.location.origin,
                    organizationId: inject(MfeDataService, { optional: true })?.organizationId$.getValue(),
                }),
            });
        }),
        {
            provide: NgbDateAdapter,
            // We pass a Date object into ngbDatePicker, we get a Date object out of it
            useClass: NgbDateNativeAdapter,
        },
        {
            provide: NgbTimeAdapter,
            useValue: {
                fromModel: (value: Date | null): NgbTimeStruct => {
                    if (!value) {
                        return {
                            hour: 0,
                            minute: 0,
                            second: 0,
                        };
                    }
                    return {
                        hour: value.getHours(),
                        minute: value.getMinutes(),
                        second: value.getSeconds(),
                    };
                },
                toModel: (time: NgbTimeStruct | null): Date => {
                    const results = new Date();
                    if (!time) {
                        results.setHours(0, 0, 0);
                        return results;
                    }
                    const { hour, minute, second } = time;
                    results.setHours(hour, minute, second);
                    return results;
                },
            },
        },
        providePrimeNG({
            theme: {
                preset: OptimizePreset,
                options: {
                    darkModeSelector: false,
                },
            },
            csp: {
                nonce: 'a1b2c3d4e5',
            },
        }),
        PrimeNGTranslationService,
        {
            provide: CSP_NONCE,
            useValue: 'a1b2c3d4e5',
        },
        ShadowDomStylesHostService,
        { provide: UseStyle, useClass: ShadowDomPrimeNgUseStyleService },
        { provide: OverlayContainer, useClass: ShadowOverlayContainer },
        {
            provide: UIROUTER_MODULE_TOKEN,
            useFactory: () => ({ states: [createAccessibilityFutureState()] }),
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
